import { useFeatureInteractionContext } from 'features/siteMap/contexts/FeatureInteractionContext'
import { MapFeatureDetailsOverlaysContainer } from 'features/siteMap/popupMenus/featureDetails/MapFeatureDetailsOverlaysContainer'
import { MapEditorOverlaysContainer } from 'features/siteMap/popupMenus/MapEditorOverlaysContainer'
import { SiteViewToggleTopOverlay } from 'features/siteMap/popupMenus/SiteViewToggleTopOverlay'
import { SiteViewMapProps } from 'features/siteMap/SiteViewMap'
import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useLayoutEffect,
} from 'react'
import { GeckoSiteModel, GeckoUnit } from 'types'

type OverlaysHandlerProps = {
  siteModel: GeckoSiteModel
  addMapFeatureMenuLocationRef?: RefObject<HTMLDivElement | null>
  showUntaggedModels: boolean
  setShowUntaggedModels: Dispatch<SetStateAction<boolean>>
  isInEditMode: boolean
  setIsInEditMode: Dispatch<SetStateAction<boolean>>
  componentToDisplayOnFeatureClick: SiteViewMapProps['componentToDisplayOnFeatureClick']
  children: React.ReactNode
  allUnitsAtSite: ReadonlyArray<GeckoUnit>
}

export const OverlaysHandler: React.FC<OverlaysHandlerProps> = (props) => {
  const { addMapFeatureMenuLocationRef, children } = props
  const [showLabels, setShowLabels] = React.useState(false)
  const [showPrimitives, setShowPrimitives] = React.useState(true)

  const { setShowFeaturePopupMenu } = useFeatureInteractionContext()

  // when isInEditMode changes, close the popup menu (use useLayoutEffect to avoid flicker)
  useLayoutEffect(() => {
    setShowFeaturePopupMenu(undefined)
  }, [props.isInEditMode, setShowFeaturePopupMenu])

  return (
    <>
      {/* -- On-Click Overlays -- */}
      {props.isInEditMode ? (
        <MapEditorOverlaysContainer
          addMapFeatureMenuLocationRef={addMapFeatureMenuLocationRef}
          siteModel={props.siteModel}
          allUnitsAtSite={props.allUnitsAtSite}
        />
      ) : (
        <MapFeatureDetailsOverlaysContainer
          componentToDisplayOnFeatureClick={
            props.componentToDisplayOnFeatureClick
          }
        />
      )}

      {/* -- Top Overlay (Mode-bar) -- */}
      <SiteViewToggleTopOverlay
        showLabels={showLabels}
        setShowLabels={setShowLabels}
        showPrimitives={showPrimitives}
        setShowPrimitives={setShowPrimitives}
        showUntaggedModels={props.showUntaggedModels}
        setShowUntaggedModels={props.setShowUntaggedModels}
        isInEditMode={props.isInEditMode}
        setIsInEditMode={props.setIsInEditMode}
      />

      {/* -- Children -- */}
      <OverlaysControlsInternalProvider
        value={{
          showLabels,
          setShowLabels,
          showPrimitives,
          setShowPrimitives,
        }}>
        {children}
      </OverlaysControlsInternalProvider>
    </>
  )
}

type OverlaysControlsContextType = {
  // The third value is a counter that is active while the viewport is animating
  showLabels: boolean
  setShowLabels: Dispatch<SetStateAction<boolean>>
  showPrimitives: boolean
  setShowPrimitives: Dispatch<SetStateAction<boolean>>
}

const OverlaysControlsContext = React.createContext<
  OverlaysControlsContextType | undefined
>(undefined)

const OverlaysControlsInternalProvider: React.FC<{
  value: OverlaysControlsContextType
  children: React.ReactNode
}> = ({ value, children }) => (
  <OverlaysControlsContext.Provider value={value}>
    {children}
  </OverlaysControlsContext.Provider>
)

export const useOverlaysControls = () => {
  const context = useContext(OverlaysControlsContext)
  if (!context)
    throw new Error(
      'useOverlaysControls () must be used within Site View MapViz'
    )
  return context
}
